import React, { useCallback, useMemo, useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import { useInfiniteQuery, useQuery, useQueryClient } from "react-query";
import { getErrorMessage } from "services/api";
import {
  getProducts,
  deleteProduct,
  getItemGroups,
  PRODUCTS_PAGE_SIZE,
} from "features/Products/api";
import { settingsAtom } from "atoms/settingsAtom";
import { activeItemsGroupAtom, selectedProductAtom } from "atoms/catalogAtom";

import { PageLayout, ActionsRow } from "components/PageLayout";
import { BoldText, RegularText } from "components/Text";
import { Button } from "components/Button";
import { Table } from "components/Table";
import { SearchInput } from "components/SearchInput";
import { useModal, ConfirmationModal, InfoModal } from "components/Modal";

import { ProductModal } from "./ProductModal";
import { MoveToGroupModal } from "./MoveToGroupModal";
import styles from "./styles.module.scss";

export const PRODUCTS_QUERY_KEY = "products";

export const ProductsPanel = () => {
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();

  const settings = useAtomValue(settingsAtom);
  const [activeGroup] = useAtom(activeItemsGroupAtom);
  const [selectedProduct, setSelectedProduct] = useAtom(selectedProductAtom);

  const [search, setSearch] = useState("");

  const groupId = Number(activeGroup?.itemgroupid || "") || undefined;

  const groupsQuery = useQuery(["item-groups"], () => getItemGroups());
  const groups = groupsQuery.data?.items ?? [];

  const productsQuery = useInfiniteQuery(
    [PRODUCTS_QUERY_KEY, groupId, search],
    ({ pageParam = 1 }) =>
      getProducts({ page: pageParam, group: groupId, search }),
    {
      getNextPageParam: (lastPage, allPages) =>
        lastPage.items.length < PRODUCTS_PAGE_SIZE
          ? undefined
          : allPages.length + 1,
      keepPreviousData: false,
    }
  );

  // Порядок страниц сохраняем как отдал бек (position, itemid).
  // Повторная сортировка + нестабильный ORDER BY давали дубли между страницами.
  const products = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const page of productsQuery.data?.pages ?? []) {
      for (const item of page.items ?? []) {
        const id = String(item.itemid);
        if (seen.has(id)) continue;
        seen.add(id);
        list.push(item);
      }
    }
    return list;
  }, [productsQuery.data]);

  const rowData = useMemo(
    () =>
      products.map((item) => ({
        itemid: item.itemid,
        name: item.name,
        price:
          item.prices?.find(
            (p) => String(p.pricelistid) === String(settings?.pricelistid)
          )?.price ?? "-",
      })),
    [products, settings]
  );

  const colDefs = useMemo(
    () => [
      { field: "itemid", headerName: "№", width: 110 },
      { field: "name", headerName: "Товар", flex: 1, minWidth: 250 },
      {
        field: "price",
        headerName: "Стоимость, ₽",
        width: 150,
        type: "rightAligned",
      },
    ],
    []
  );

  // Подгрузка следующей страницы при прокрутке к концу таблицы
  const handleBodyScrollEnd = useCallback(
    (event) => {
      if (!productsQuery.hasNextPage || productsQuery.isFetchingNextPage) {
        return;
      }
      const lastVisible = event.api.getLastDisplayedRowIndex
        ? event.api.getLastDisplayedRowIndex()
        : event.api.getLastDisplayedRow?.();
      if (
        typeof lastVisible === "number" &&
        lastVisible >= rowData.length - 30
      ) {
        productsQuery.fetchNextPage();
      }
    },
    [productsQuery, rowData.length]
  );

  const openProduct = (row) => {
    const product = products.find((p) => p.itemid === row.itemid);
    if (!product) return;
    setSelectedProduct(product);
    openModal(<ProductModal isEdit groups={groups} />);
  };

  const onAddClick = () => {
    // «Все товары» — тоже валидный выбор: товар создаётся в корне
    setSelectedProduct(null);
    openModal(<ProductModal isEdit={false} groups={groups} />);
  };

  const onPropertiesClick = () => {
    if (!selectedProduct) return;
    openModal(<ProductModal isEdit groups={groups} />);
  };

  const onMoveClick = () => {
    if (!selectedProduct || !groups.length) return;
    openModal(<MoveToGroupModal mode="product" groups={groups} />);
  };

  const onDeleteClick = () => {
    if (!selectedProduct) return;
    openModal(
      <ConfirmationModal
        title={`Вы действительно хотите удалить товар "${selectedProduct.name}"?`}
        onConfirm={async () => {
          try {
            await deleteProduct(selectedProduct.itemid);
            setSelectedProduct(null);
            queryClient.invalidateQueries([PRODUCTS_QUERY_KEY]);
            closeModal();
          } catch (e) {
            openModal(
              <InfoModal title={getErrorMessage(e)} onClose={closeModal} />
            );
          }
        }}
        onClose={closeModal}
      />
    );
  };

  return (
    <PageLayout
      className={styles.productsPanel}
      header={<BoldText>Товары</BoldText>}
      footer={
        <ActionsRow>
          <Button
            icon="plus"
            variant="secondary"
            onClick={onAddClick}
            title="Добавить товар"
          />
          <Button icon="move" onClick={onMoveClick} disabled={!selectedProduct}>
            Переместить
          </Button>
          <Button
            icon="settings"
            onClick={onPropertiesClick}
            disabled={!selectedProduct}
          >
            Свойства
          </Button>
          <Button
            icon="delete"
            onClick={onDeleteClick}
            disabled={!selectedProduct}
            title="Удалить товар"
          />
        </ActionsRow>
      }
      bodyFill
    >
      <div className={styles.productsSearch}>
        <SearchInput value={search} onChange={setSearch} placeholder="Поиск" />
      </div>
      <Table
        fill
        rowData={rowData}
        colDefs={colDefs}
        rowKey="itemid"
        selectedId={selectedProduct?.itemid}
        onSelect={(row) => {
          const product = products.find((p) => p.itemid === row.itemid);
          setSelectedProduct(product || null);
        }}
        onOpen={openProduct}
        loading={productsQuery.isLoading}
        error={
          productsQuery.isError ? getErrorMessage(productsQuery.error) : ""
        }
        onRetry={productsQuery.refetch}
        onBodyScrollEnd={handleBodyScrollEnd}
      />
      {productsQuery.isFetchingNextPage && (
        <RegularText size={13} color="muted" className={styles.loadingMore}>
          Загрузка...
        </RegularText>
      )}
    </PageLayout>
  );
};
